import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, ArrowLeft, CheckCircle2 } from "lucide-react";
import Navigation from "../../components/sections/Navigation";
import { Footer } from "../../components";
import Button from "../../components/ui/Button";

const Privacy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    document.documentElement.classList.add("dark");
    return () => {
      document.documentElement.classList.remove("dark");
    };
  }, []);

  const sections = [
    {
      title: "1. Introduction",
      content: `FaceTraits ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered facial analysis service. Please read this Privacy Policy carefully. By using our Service, you agree to the collection and use of information in accordance with this policy.`,
    },
    {
      title: "2. Information We Collect",
      content: `We collect information that you provide directly to us and information that is automatically collected when you use our Service:

- Account Information: When you create an account, we collect your email address, name, and authentication credentials.
- Image Data: When you upload images for analysis, we process the images temporarily to provide our AI analysis services.
- Usage Data: We collect information about how you interact with our Service, including features used, analysis history, and subscription information.
- Payment Information: For paid subscriptions, we collect billing information through our payment processor (Stripe). We do not store your full payment card details on our servers.
- Device Information: We may collect information about your device, including IP address, browser type, and operating system.`,
    },
    {
      title: "3. How We Use Your Information",
      content: `We use the information we collect to:

- Provide and maintain our Service, including processing your image uploads and delivering analysis results
- Process your subscription payments and manage your account
- Send you service-related communications, including updates and support messages
- Improve and optimize our Service, including developing new features and analyzing usage patterns
- Detect, prevent, and address technical issues and security threats
- Comply with legal obligations and enforce our Terms of Service`,
    },
    {
      title: "4. Image Processing and Storage",
      content: `Privacy is our top priority when it comes to your images:

- Temporary Processing: Images are processed in real-time for analysis purposes only
- No Permanent Storage: We do not store your uploaded images after analysis is complete
- Secure Processing: All image processing occurs in secure, encrypted environments
- No Sharing: We do not share, sell, or use your images for any purpose other than providing the analysis you requested
- Deletion: Images are automatically deleted from our servers immediately after processing`,
    },
    {
      title: "5. Data Sharing and Disclosure",
      content: `We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:

- Service Providers: We may share information with trusted third-party service providers who assist us in operating our Service (e.g., payment processing, cloud hosting), subject to strict confidentiality agreements
- Legal Requirements: We may disclose information if required by law, court order, or government regulation
- Business Transfers: In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction
- With Your Consent: We may share information with your explicit consent for specific purposes`,
    },
    {
      title: "6. Data Security",
      content: `We implement industry-standard security measures to protect your information:

- Encryption: All data transmissions are encrypted using SSL/TLS protocols
- Secure Storage: Personal information is stored in secure, encrypted databases
- Access Controls: We limit access to your personal information to authorized personnel only
- Regular Audits: We conduct regular security audits and assessments
- Incident Response: We have procedures in place to respond to security incidents promptly

However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.`,
    },
    {
      title: "7. Your Privacy Rights",
      content: `Depending on your location, you may have certain rights regarding your personal information:

- Access: You can request access to the personal information we hold about you
- Correction: You can request correction of inaccurate or incomplete information
- Deletion: You can request deletion of your personal information, subject to legal and operational requirements
- Portability: You can request a copy of your data in a portable format
- Opt-Out: You can opt out of certain communications, such as marketing emails
- Account Deletion: You can delete your account at any time through your account settings

To exercise these rights, please contact us through our support channels.`,
    },
    {
      title: "8. Cookies and Tracking Technologies",
      content: `We use cookies and similar tracking technologies to:

- Maintain your session and authentication state
- Remember your preferences and settings
- Analyze Service usage and performance
- Provide personalized experiences

You can control cookies through your browser settings, but disabling cookies may affect certain features of our Service.`,
    },
    {
      title: "9. Third-Party Services",
      content: `Our Service may contain links to third-party websites or integrate with third-party services:

- Payment Processing: We use Stripe for payment processing. Stripe's privacy policy applies to payment transactions
- Analytics: We may use analytics services to understand how our Service is used
- Authentication: We use Supabase for authentication services

We are not responsible for the privacy practices of third-party services. We encourage you to review their privacy policies.`,
    },
    {
      title: "10. Children's Privacy",
      content: `Our Service is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If we become aware that we have collected information from a child under 13, we will take steps to delete that information promptly. If you believe we have collected information from a child under 13, please contact us immediately.`,
    },
    {
      title: "11. International Data Transfers",
      content: `Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from those in your country. By using our Service, you consent to the transfer of your information to these countries. We take appropriate safeguards to ensure your information receives adequate protection.`,
    },
    {
      title: "12. Data Retention",
      content: `We retain your personal information only for as long as necessary to:

- Provide our Service to you
- Fulfill the purposes outlined in this Privacy Policy
- Comply with legal obligations
- Resolve disputes and enforce our agreements

Account information is retained while your account is active. After account deletion, we may retain certain information as required by law or for legitimate business purposes.`,
    },
    {
      title: "13. Changes to This Privacy Policy",
      content: `We may update this Privacy Policy from time to time. We will notify you of any material changes by:

- Posting the new Privacy Policy on this page
- Updating the "Last Updated" date
- Sending you an email notification (for significant changes)
- Displaying a notice on our Service

Your continued use of our Service after changes become effective constitutes acceptance of the updated Privacy Policy.`,
    },
    {
      title: "14. Contact Us",
      content: `If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us through our support channels. We will respond to your inquiry as soon as possible.`,
    },
  ];

  return (
    <div className="min-h-screen bg-stone-950 text-zinc-100 font-sans">
      <Navigation />

      <section className="relative pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-rose-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-4xl mx-auto relative">
          {/* Header */}
          <div className="mb-8">
            <Button
              onClick={() => navigate(-1)}
              variant="ghost"
              className="mb-6 flex items-center gap-2 text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>

            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-lg bg-teal-500/20 text-teal-400">
                <Shield className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-4xl sm:text-5xl font-bold mb-2 bg-gradient-to-r from-zinc-100 via-teal-100 to-rose-100 bg-clip-text text-transparent">
                  Privacy Policy
                </h1>
                <p className="text-zinc-400 text-sm">
                  Last Updated:{" "}
                  {new Date().toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              </div>
            </div>

            <div className="p-4 rounded-lg bg-teal-500/10 border border-teal-500/20 mb-8">
              <p className="text-sm text-zinc-300">
                Your privacy is important to us. This Privacy Policy explains
                how FaceTraits collects, uses, and protects your personal
                information when you use our Service.
              </p>
            </div>
          </div>

          {/* Privacy Sections */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <div
                key={index}
                className="rounded-xl p-6 sm:p-8 border border-zinc-800 bg-stone-900/30 hover:border-zinc-700 transition-colors"
              >
                <h2 className="text-2xl font-bold mb-4 text-teal-400 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />
                  {section.title}
                </h2>
                <p className="text-zinc-300 leading-relaxed whitespace-pre-line">
                  {section.content}
                </p>
              </div>
            ))}
          </div>

          {/* Footer Note */}
          <div className="mt-8 text-center">
            <p className="text-sm text-zinc-500">
              If you have any questions about this Privacy Policy, please{" "}
              <button
                onClick={() => {
                  if (window.Tawk_API && window.Tawk_API.maximize) {
                    window.Tawk_API.maximize();
                  }
                }}
                className="text-teal-400 hover:text-teal-300 underline"
              >
                contact our support team
              </button>
              .
            </p>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Privacy;
